import type { ReactNode } from 'react';
import { Button } from '../Button/Button';
import { SparkIcon } from '../SparkIcon';
import { FeedbackIcon } from '../shared/FeedbackIcon';
import type { FeedbackType } from '../shared/feedbackHelpers';
import './Toast.css';
import '../shared/feedbackIcons.css';

/** Transient notification toast with a feedback icon. Supports simple (title-only) and detailed (title + content) layouts. */
export type ToastProps = {
  title?: ReactNode;
  content?: ReactNode;
  feedbackType?: FeedbackType;
  /** Replaces the feedback icon in the detailed layout, e.g. `<SparkIcon slug="bell" size="medium" />`. */
  icon?: ReactNode;
  detailed?: boolean;
  showButtons?: boolean;
  /** Detailed layout: show the ghost button before the action button. */
  showCloseButton?: boolean;
  showIconButton?: boolean;
  closeLabel?: string;
  actionLabel?: string;
  className?: string;
  onCloseClick?: () => void;
  onActionClick?: () => void;
  onDismissClick?: () => void;
};

export function Toast({
  title = 'Notification title',
  content = 'Interactively monetize corporate alignments and fully tested niche markets.',
  feedbackType = 'success',
  icon,
  detailed = false,
  showButtons = true,
  showCloseButton = true,
  showIconButton = true,
  closeLabel = 'Close',
  actionLabel = 'Action',
  className = '',
  onCloseClick,
  onActionClick,
  onDismissClick,
}: ToastProps) {
  if (detailed) {
    return (
      <div
        className={['spark-toast', 'spark-toast-detailed', `spark-toast-${feedbackType}`, className]
          .filter(Boolean)
          .join(' ')}
        role="status"
      >
        <div className="spark-toast-detailed-header">
          <div className="spark-toast-detailed-main">
            <div className="spark-toast-title-row">
              {icon ? (
                <span className="spark-toast-icon spark-toast-custom-icon">{icon}</span>
              ) : (
                <FeedbackIcon feedbackType={feedbackType} className="spark-toast-icon" />
              )}
              <p className="spark-toast-detailed-title">{title}</p>
            </div>
            <div className="spark-toast-detailed-content">{content}</div>
          </div>
          {showIconButton ? (
            <Button
              variant="ghost"
              size="medium"
              type="button"
              className="spark-toast-icon-close"
              aria-label="Dismiss toast"
              onClick={onDismissClick ?? onCloseClick}
              icon={<SparkIcon slug="close" size="small" />}
            />
          ) : null}
        </div>
        {showButtons ? (
          <div className="spark-toast-footer">
            {showCloseButton ? (
              <Button variant="ghost" size="tiny" type="button" onClick={onCloseClick}>
                {closeLabel}
              </Button>
            ) : null}
            <Button variant="primary" size="tiny" type="button" onClick={onActionClick}>
              {actionLabel}
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={['spark-toast', `spark-toast-${feedbackType}`, className].filter(Boolean).join(' ')}
      role="status"
    >
      <div className="spark-toast-simple-content">
        <FeedbackIcon feedbackType={feedbackType} className="spark-toast-icon" />
        <p className="spark-toast-simple-title">{title}</p>
      </div>
      {showButtons ? (
        <div className="spark-toast-simple-actions">
          <Button variant="tertiary" size="tiny" type="button" onClick={onCloseClick}>
            {closeLabel}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
